import type { StudyDeliveryQuestionV2, StudyDeliveryV2 } from '../learningEngineV2/studyDeliveryContract';
import type { DailyReasonCode } from '../../types/learning';
import { buildLongMultiplicationWorkTemplate } from './proceduralScaffold';
import type { DifficultyBand, MathWorksheetProblemV2, MathWorksheetSetV2 } from './worksheetTypes';
import { deriveWorksheetSequenceMetaV2, WORKSHEET_SURFACE_PROFILE_V2 } from './worksheetSurface';

function promptParts(prompt: unknown) {
  if (typeof prompt === 'string') return { text: prompt, supportingText: '' };
  if (prompt && typeof prompt === 'object' && !Array.isArray(prompt)) {
    const row = prompt as Record<string, unknown>;
    return {
      text: typeof row.text === 'string' ? row.text : typeof row.prompt === 'string' ? row.prompt : 'Soal BelajarKu',
      supportingText: typeof row.supportingText === 'string' ? row.supportingText : '',
    };
  }
  return { text: 'Soal BelajarKu', supportingText: '' };
}

function difficultyBand(value: unknown): DifficultyBand {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const band = (value as Record<string, unknown>).band;
    if (band === 'D1' || band === 'D2' || band === 'D3' || band === 'D4') return band;
  }
  return 'D2';
}

function reasonCode(value: unknown): DailyReasonCode {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const reason = (value as Record<string, unknown>).reasonCode;
    if (reason === 'remediation' || reason === 'mastery_check' || reason === 'spaced_review' || reason === 'current_practice') return reason;
  }
  return 'current_practice';
}

function placement(indexOnPage: number) {
  const column = indexOnPage % 2;
  const row = Math.floor(indexOnPage / 2);
  return {
    x: column === 0 ? 0.08 : 0.54,
    y: 0.14 + row * 0.27,
    width: 0.38,
    height: 0.22,
  };
}


function multiplicationOperands(question: StudyDeliveryQuestionV2, text: string): [number, number] | null {
  const skillCode = question.skill.code ?? '';
  if (!skillCode.includes('-MUL-')) return null;
  const match = text.match(/^\s*(\d+)\s*[×x*]\s*(\d+)\s*$/u);
  if (!match) return null;
  const a = Number(match[1]);
  const b = Number(match[2]);
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b)) return null;
  return [a, b];
}

function adaptQuestion(question: StudyDeliveryQuestionV2, index: number, total: number): MathWorksheetProblemV2 {
  const prompt = promptParts(question.prompt);
  const multiply = multiplicationOperands(question, prompt.text);
  return {
    problemId: question.questionInstanceId,
    skillCode: question.skill.code ?? 'MTH-UNSPECIFIED',
    operation: multiply ? 'multiplication' : 'generic_numeric',
    multiplicand: multiply?.[0] ?? 0,
    multiplier: multiply?.[1] ?? 0,
    displayPrompt: prompt.text,
    supportingText: prompt.supportingText || undefined,
    difficulty: difficultyBand(question.difficulty),
    scaffold: 'S0',
    stage: 'INDEPENDENT_PRACTICE',
    reasonCode: reasonCode(question.difficulty),
    workGuideProfile: 'STANDARD',
    placement: placement(index % 5),
    workTemplate: multiply ? buildLongMultiplicationWorkTemplate(multiply[0], multiply[1]) : undefined,
    generatorVersion: question.generatorVersion,
    contentVersion: `worksheet-version:${question.assignmentItemId}`,
    sequenceMeta: deriveWorksheetSequenceMetaV2(index, total),
    submissionBinding: {
      assignmentItemId: question.assignmentItemId,
      questionInstanceId: question.questionInstanceId,
      sequence: question.assignmentSequence,
    },
  };
}

export function adaptStudyDeliveryToMathWorksheet(delivery: StudyDeliveryV2): MathWorksheetSetV2 {
  const problems = delivery.questions
    .slice()
    .sort((a, b) => a.assignmentSequence - b.assignmentSequence || a.questionSequence - b.questionSequence)
    .map((question, index, rows) => adaptQuestion(question, index, rows.length));
  if (!problems.length) throw new Error('Study Delivery contains no Mathematics questions.');

  return {
    worksheetSetId: delivery.worksheet.worksheetId,
    subjectCode: 'MTH',
    worksheetVersion: delivery.worksheet.worksheetVersionId,
    mode: 'WORKSHEET',
    feedbackPolicy: 'DELAYED_WORKSHEET',
    surface: WORKSHEET_SURFACE_PROFILE_V2,
    pages: Array.from({ length: Math.ceil(problems.length / 5) }, (_, index) => ({
      pageId: `${delivery.worksheet.worksheetVersionId}-PAGE-${index + 1}`,
      pageNumber: index + 1,
      problems: problems.slice(index * 5, index * 5 + 5),
    })),
  };
}
